import { useCommonClient } from '@/app/common/commonClientProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { css } from 'styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { DeprecatedLinkButton as LinkButton } from '@lmn/design-system';
import { URLParameters } from '@/app/common/urlParameters';
import CustomCalendarBody from '@/app/common/components/Modals/CustomCalendar/CustomCalendarBody';
import { MODAL_SIZES } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import { CustomDatePickerHeader } from '../../common/components/Modals/CustomCalendar/CustomDatePickerHeader';
import { useTranslation } from '../translations/Translation';
import { type DatePickersProps } from '../search/components/DatePickers';
import BasicModalHeader from '../search/components/Modal/LayoutElements/BasicModalHeader';
import OpenXModal from '../search/components/Modal/OpenXModal';
import BasicModalFooter from '../search/components/Modal/LayoutElements/BasicModalFooter';
import { changeDateFormat, changeUrlDepartureIntervalsFormat, getNights } from './common/dates';
import type { ClickParams } from '../search/Curiosity/Analytics/utils';
import { useUrlSearchParams } from './UrlSearchParamsProvider';
import type { Dates } from '../search/utils';
import { DatesType, DateType } from '../search/utils';
import getSearchParams from '../search/common/getSearchParams';
import { Cta } from '../search/components/Modal/LayoutElements/Cta/styled';
import { useOffersContext } from './OffersProvider';

export function InlineDatePicker() {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);

  const onShowCalendarClick = () => {
    setOpen(true);
  };

  return (
    <>
      <LinkButton
        variant="accent"
        onClick={onShowCalendarClick}
        css={css`
          padding: 0;
          ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
        `}
      >
        {t('vacation.select.summary.modify')}
      </LinkButton>
      <InlineDatePickerModal isOpen={isOpen} onClose={() => setOpen(false)} onOpen={() => setOpen(true)} />
    </>
  );
}

export function InlineDatePickerModal(props: {
  isOpen: boolean;
  returnDatePreselected?: boolean;
  onClose: () => void;
  onOpen: () => void;
}) {
  const { isOpen, onClose, onOpen, returnDatePreselected = false } = props;
  const { localization } = usePageWrapperData();
  const { isMobile } = useCommonClient();
  const { t } = useTranslation();
  const searchParams = getSearchParams();
  const { resetOffers } = useOffersContext();
  const { checkin, checkout, setDates } = useUrlSearchParams();

  const departureDate = changeUrlDepartureIntervalsFormat(checkin);
  const returnDate = changeUrlDepartureIntervalsFormat(checkout);

  const defaultDates: Dates = {
    departureDate: { date: departureDate, type: DateType.EXACT },
    returnDate: { date: returnDate, type: DateType.EXACT },
    flexDate: { type: DateType.ANYTIME },
    subDate: 0,
    type: DatesType.CALENDAR,
  };

  const [selectedInput, setSelectedInput] = useState(returnDatePreselected ? 2 : 1);
  const [customDates, setCustomDates] = useState<Dates | undefined>(defaultDates);
  const [returnDateWasPreselected, setReturnDateWasPreselected] = useState(returnDatePreselected);
  const newDates = useRef<{ departureDate: string | undefined; returnDate: string | undefined }>(undefined);

  const hasSelectedDates = !!customDates?.departureDate?.date && !!customDates?.returnDate?.date;
  const className = `${isMobile ? 'inline-date-picker-modal-mobile mobile-modal-full-height' : 'inline-date-picker-modal'}`;

  useEffect(() => {
    setCustomDates(defaultDates);
  }, [searchParams[URLParameters.TRIP_DATES]]);

  useEffect(() => {
    setSelectedInput(returnDatePreselected ? 2 : 1);
    setReturnDateWasPreselected(returnDatePreselected);
  }, [returnDatePreselected]);

  const onModalClose = () => {
    setCustomDates(defaultDates);
    onClose();
  };

  const onApply = () => {
    if (customDates?.departureDate?.date !== departureDate || customDates?.returnDate?.date !== returnDate) {
      newDates.current = { departureDate: customDates?.departureDate?.date, returnDate: customDates?.returnDate?.date };
    }
    onClose();
  };

  /**
   * Waiting for the modal to fully close and go back in history.
   * Changing the dates modifies the url params, we need to modify them after history navigation goes back to the
   * the step previous to the modal opened.
   */
  const onFinallyHistoryBack = () => {
    if (!newDates.current) return;
    const newCheckin = newDates.current.departureDate
      ? changeDateFormat(newDates.current.departureDate, 'yyyy-MM-dd', 'yyyyMMdd')
      : undefined;
    const newCheckout = newDates.current.returnDate
      ? changeDateFormat(newDates.current.returnDate, 'yyyy-MM-dd', 'yyyyMMdd')
      : undefined;
    const newNights = newCheckin && newCheckout ? getNights(newCheckin, newCheckout) : undefined;
    newDates.current = undefined;
    if (newCheckin && newCheckout && newNights) {
      resetOffers();
      // Defer to the next event loop tick to ensure history.back() has fully settled
      // window.location.href before setNewDates reads it to call updateUrl (replaceState).
      // Without this, the browser may fire popstate before
      // the URL reflects the back-navigated entry, causing replaceState to act on the
      // wrong history entry and leaving date params (departureIntervals, dateFrom, dateTo)
      // unchanged in the URL.
      setTimeout(() => {
        setDates({ checkin: newCheckin, checkout: newCheckout, nights: newNights, origin: 'DATES_PICKER' });
      }, 0);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEvent = (e: ClickParams) => {
    // TODO: send event?
    // clickEvent({
    //   localization,
    //   curiosityParams,
    //   clickParams: {
    //     ...e,
    //     // replace original event name with custom_checkin_checkout
    //     event_name: e.event_name.replace('searchwidget_calendar', 'custom_checkin_checkout'),
    //   },
    //   routeType: RouteType.OSMAP,
    // });
  };

  const datePickersProps: DatePickersProps = {
    name: 'calendar',
    dates: customDates,
    selectedInput,
    hasAvailability: true,
    hasDates: true,
    showFlexButtons: false,
    disableDatesOutsideBookingWindow: true,
    setDates: setCustomDates,
    setSelectedInput,
    eventCallback: handleEvent,
    transportData: undefined,
    focusOnActiveDates: isMobile,
    getCalendarHeight: (fixedHeadersHeight: number) => `calc(100vh - ${fixedHeadersHeight + 80}px)`,
    returnDateWasPreselected,
    setReturnDateWasPreselected,
  };

  return (
    <OpenXModal
      isOpen={isOpen}
      onClose={onModalClose}
      onCloseByBrowserHistory={onModalClose}
      onOpenByBrowserHistory={onOpen}
      onFinallyHistoryBack={onFinallyHistoryBack}
      className={className}
      size={MODAL_SIZES.XXL}
      id="inline-date-picker-modal-id"
      bodyPadding={2}
      bodyPaddingS={2}
      fullScreen={false}
      isMobile={isMobile}
      brand={localization.brand}
      bodyBackgroundColor={isMobile ? 'backgroundLayer01' : undefined}
      aria-label={t('vacation.select.calendar.select')}
      header={
        isMobile
          ? {
              sticky: true,
              element: (
                <BasicModalHeader handleClose={onModalClose}>{t('vacation.select.calendar.select')}</BasicModalHeader>
              ),
            }
          : {
              sticky: true,
              element: (
                <CustomDatePickerHeader dates={customDates} returnDateWasPreselected={returnDateWasPreselected} />
              ),
            }
      }
      footer={{
        sticky: true,
        element: (
          <BasicModalFooter
            handleApply={onApply}
            isApplyDisabled={!hasSelectedDates}
            applyText={t('vacation.common.price.check')}
            css={`
              ${Cta} {
                ${
                  isMobile
                    ? css`
                        width: 100%;
                      `
                    : css``
                }
              }
            `}
          />
        ),
      }}
    >
      <CustomCalendarBody
        customDates={customDates}
        returnDateWasPreselected={returnDateWasPreselected}
        datePickersProps={datePickersProps}
      />
    </OpenXModal>
  );
}
